import { UserIdentity, DEFAULT_IDENTITY, UserRole } from "../types/user";
import { DatabaseService } from "./database";

/**
 * Service to manage user identity (reviewer/reviewee roles)
 */
export class IdentityService {
  /**
   * Get the current user's identity from the database
   * @param userId The user ID
   * @returns The user's identity object
   */
  static async getUserIdentity(userId: string): Promise<UserIdentity> {
    try {
      console.log("🔍 IdentityService: Fetching user data for:", userId);
      const user = await DatabaseService.getCurrentUser(userId);
      console.log("🔍 IdentityService: User data from DB:", user);

      if (user && user.roles) {
        console.log("🔍 IdentityService: User roles from DB:", user.roles);
        // Map database roles to our UserRole types
        const validRoles: UserRole[] = user.roles.filter(
          (role): role is UserRole => role === "reviewer" || role === "reviewee"
        );
        console.log("🔍 IdentityService: Valid roles:", validRoles);

        const identity = {
          roles: validRoles.length > 0 ? validRoles : DEFAULT_IDENTITY.roles,
          isReviewer: validRoles.includes("reviewer"),
          isReviewee: validRoles.includes("reviewee"),
        };
        console.log("🔍 IdentityService: Final identity:", identity);
        return identity;
      }

      console.log(
        "🔍 IdentityService: No user or no roles found, using default"
      );
    } catch (error) {
      console.error("Error getting user identity from database:", error);
    }

    // Return default identity if none found
    console.log(
      "🔍 IdentityService: Returning default identity:",
      DEFAULT_IDENTITY
    );
    return { ...DEFAULT_IDENTITY };
  }

  /**
   * Get the current user's identity synchronously (for backwards compatibility)
   * This should be phased out in favor of the async version
   * @param userId The user ID
   * @returns The user's identity object (may be stale)
   */
  static getUserIdentitySync(userId: string): UserIdentity {
    // For backwards compatibility, return default identity
    // Components should migrate to the async version
    return { ...DEFAULT_IDENTITY };
  }

  /**
   * Save the user's roles to the database
   * @param userId The user ID
   * @param roles The roles to save
   * @returns The updated identity
   */
  static async saveUserRoles(
    userId: string,
    roles: UserRole[]
  ): Promise<UserIdentity | null> {
    try {
      const updatedUser = await DatabaseService.updateUser(userId, { roles });
      if (updatedUser) {
        return {
          roles,
          isReviewer: roles.includes("reviewer"),
          isReviewee: roles.includes("reviewee"),
        };
      }
    } catch (error) {
      console.error("Error saving user roles to database:", error);
    }
    return null;
  }

  /**
   * Add a role to the user's identity
   * @param userId The user ID
   * @param role The role to add
   * @returns The updated identity
   */
  static async addRole(
    userId: string,
    role: UserRole
  ): Promise<UserIdentity | null> {
    try {
      const identity = await this.getUserIdentity(userId);

      // If the role already exists, return the current identity
      if (identity.roles.includes(role)) {
        return identity;
      }

      // Add the role and save to database
      const updatedRoles = [...identity.roles, role];
      return await this.saveUserRoles(userId, updatedRoles);
    } catch (error) {
      console.error("Error adding role:", error);
      return null;
    }
  }

  /**
   * Remove a role from the user's identity
   * @param userId The user ID
   * @param role The role to remove
   * @returns The updated identity
   */
  static async removeRole(
    userId: string,
    role: UserRole
  ): Promise<UserIdentity | null> {
    try {
      const identity = await this.getUserIdentity(userId);

      // If the role doesn't exist, return the current identity
      if (!identity.roles.includes(role)) {
        return identity;
      }

      // Remove the role and save to database
      const updatedRoles = identity.roles.filter((r) => r !== role);
      return await this.saveUserRoles(userId, updatedRoles);
    } catch (error) {
      console.error("Error removing role:", error);
      return null;
    }
  }

  /**
   * Check if a user has a specific role
   * @param userId The user ID
   * @param role The role to check
   * @returns True if the user has the role
   */
  static async hasRole(userId: string, role: UserRole): Promise<boolean> {
    try {
      const identity = await this.getUserIdentity(userId);
      return identity.roles.includes(role);
    } catch (error) {
      console.error("Error checking user role:", error);
      return false;
    }
  }

  /**
   * Check if a user has a specific role (synchronous version for backwards compatibility)
   * @param userId The user ID
   * @param role The role to check
   * @returns True if the user has the role
   */
  static hasRoleSync(userId: string, role: UserRole): boolean {
    // For backwards compatibility, return false
    // Components should migrate to the async version
    return false;
  }

  /**
   * Get the default landing page for a user based on their identity
   * @param userId The user ID
   * @returns The path to redirect to
   */
  static async getDefaultLandingPage(userId: string): Promise<string> {
    try {
      const identity = await this.getUserIdentity(userId);

      // If user is a reviewer, go to reviewer-dashboard
      if (identity.isReviewer) {
        return "/reviewer-dashboard";
      }

      // Otherwise, go to my-essays
      return "/applicant-dashboard";
    } catch (error) {
      console.error("Error determining default landing page:", error);
      return "/applicant-dashboard";
    }
  }
}
